/**
 * Pure display helper functions extracted from page components.
 * No framework dependencies — just inputs → strings/objects.
 */

/**
 * Map task points (1-5) to a Tailwind background-color class.
 *
 * Extracted from TodayPage, TaskDetailPage, CreateTaskPage,
 * CompleteTaskPage, SortableTaskList.
 */
export const getDifficultyColor = (points: number): string => {
  if (points <= 1) return 'bg-green-500';
  if (points <= 2) return 'bg-blue-500';
  if (points <= 3) return 'bg-yellow-500';
  if (points <= 4) return 'bg-orange-500';
  return 'bg-purple-500';
};

/**
 * Map task points (1-5) to a human-readable difficulty label.
 *
 * Extracted from TaskDetailPage, CreateTaskPage, CompleteTaskPage.
 */
export const getDifficultyLabel = (points: number): string => {
  if (points === 1) return 'Very Easy';
  if (points === 2) return 'Easy';
  if (points === 3) return 'Medium';
  if (points === 4) return 'Hard';
  return 'Very Hard';
};

/**
 * Return a motivational message based on the current streak length.
 *
 * Extracted from TodayPage.
 */
export const getStreakMessage = (streak: number): string => {
  if (streak === 0) return 'Start your streak today!';
  if (streak === 1) return 'Great start! Keep it going!';
  if (streak < 7) return 'Building momentum!';
  if (streak < 30) return "You're on fire!";
  if (streak < 100) return 'Unstoppable!';
  return 'Legendary status!';
};

export interface TaskStatus {
  color: string;
  bg: string;
  message: string;
}

/**
 * Derive a task's visual status from its dirty percentage.
 *
 * Extracted from OutstandingTaskCard in TodayPage.
 */
export const getTaskStatus = (dirtyPercent: number): TaskStatus => {
  if (dirtyPercent >= 90)
    return { color: 'text-red-400', bg: 'bg-red-900/20', message: 'Time for action' };
  if (dirtyPercent >= 60)
    return { color: 'text-yellow-400', bg: 'bg-yellow-900/20', message: 'Needs attention' };
  return { color: 'text-green-400', bg: 'bg-green-900/20', message: 'Shiny clean' };
};

/**
 * Format a frequency number + unit into a readable string.
 *
 * Examples: (1, "days") → "daily", (2, "weeks") → "biweekly",
 *           (3, "months") → "every 3 months"
 *
 * Extracted from ConfirmationCard.tsx.
 */
export const formatFrequency = (num: number, unit: string): string => {
  if (num === 1) {
    switch (unit) {
      case 'days':
        return 'daily';
      case 'weeks':
        return 'weekly';
      case 'months':
        return 'monthly';
      case 'years':
        return 'yearly';
    }
  }
  if (num === 2 && unit === 'weeks') return 'biweekly';
  const singular = unit.endsWith('s') ? unit.slice(0, -1) : unit;
  return num === 1 ? singular : `every ${num} ${unit}`;
};
